'use client';

//https://locize.com/blog/next-app-dir-i18n/
import dayjs from 'dayjs';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useEffect, useState } from 'react';
import type { UseTranslationOptions } from 'react-i18next';
import { initReactI18next, useTranslation as useTranslationOrg } from 'react-i18next';

import { getOptions, languages } from './settings';

const runsOnServerSide = typeof window === 'undefined';

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./locales/${['zh_tw'].includes(language) ? 'zh-Hant' : language}/${namespace}.json`)
    )
  )
  .init({
    ...getOptions(),
    react: {
      bindI18nStore: 'added'
    },
    detection: {
      order: ['cookie', 'localStorage', 'navigator'],
      lookupCookie: 'i18next',
      lookupLocalStorage: 'i18nextLng'
    },
    preload: runsOnServerSide ? languages : []
  });

const initDayjsLocale = () => {
  const lng = i18next.language || 'en';
  const dayjsLocale = lng.startsWith('zh') || lng.startsWith('yue') ? 'zh-cn' : 'en';

  dayjs.locale(dayjsLocale);
};

// 页面加载时初始化
initDayjsLocale();

// 语言切换时同步更新
i18next.on('languageChanged', (lng) => {
  const dayjsLocale = lng.startsWith('zh') || lng.startsWith('yue') ? 'zh-cn' : 'en';

  dayjs.locale(dayjsLocale);
});

export function useTranslation(
  lng?: string | undefined,
  ns?: string | undefined,
  options?: UseTranslationOptions<undefined> | undefined
) {
  const ret = useTranslationOrg(ns, options);
  const { i18n } = ret;

  if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (activeLng === i18n.resolvedLanguage) return;

      setActiveLng(i18n.resolvedLanguage);
    }, [activeLng, i18n.resolvedLanguage]);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!lng || i18n.resolvedLanguage === lng) return;

      i18n.changeLanguage(lng);
    }, [lng, i18n]);
  }

  return ret;
}
